import { ChangeDetectorRef, Component, HostListener, Input, OnChanges } from '@angular/core';

export interface HeatAxis {
  labels: string[];      // full labels (used in tooltips)
  short?: string[];      // optional shorter labels for axis ticks
  ticks?: number[];      // which indices to show as ticks (default: all)
}
export interface HeatView {
  matrix: number[][];    // matrix[rowIndex][colIndex]
  rows: HeatAxis;        // y axis
  cols: HeatAxis;        // x axis
}

interface CellVm {
  color: string;
  tooltip: string;
}
interface RowVm {
  label: string;
  cells: CellVm[];
}
interface TickVm {
  col: number;                       // 0-based column index
  label: string;
  align: 'start' | 'center' | 'end';
}

@Component({
  selector: 'ipm-heatmap',
  templateUrl: './heatmap.component.html',
  styleUrls: ['./heatmap.component.scss']
})
export class HeatmapComponent implements OnChanges {
  @Input() view!: HeatView;             // desktop / default
  @Input() compactView?: HeatView;      // optional layout for small screens
  @Input() breakpoint = 640;

  @Input() title = '';
  @Input() subtitle = '';
  @Input() compactSubtitle?: string;
  @Input() valueLabel = 'value';        // tooltip metric name

  @Input() lowColor = '#151515';
  @Input() highColor = '#8c7af2';
  @Input() gap = 6;
  @Input() borderRadius = 7;

  @Input() legendLow = 'Lower Performance';
  @Input() legendHigh = 'Higher Performance';
  @Input() legendLowCompact = 'LOW PERF';
  @Input() legendHighCompact = 'HIGH PERF';

  legendSwatches: string[] = [];
  rowsVm: RowVm[] = [];
  colTicksVm: TickVm[] = [];
  colCount = 0;
  compactActive = false;

  private low: number[] = [];
  private high: number[] = [];
  private max = 0;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(): void {
    this.build();
  }

  // switch between desktop / compact layouts as the viewport crosses the breakpoint
  @HostListener('window:resize') onResize(): void {
    if (this.useCompact() !== this.compactActive) {
      this.build();
      this.cdr.detectChanges();
    }
  }

  private useCompact(): boolean {
    return !!this.compactView && window.innerWidth <= this.breakpoint;
  }
  private active(): HeatView {
    return this.useCompact() ? this.compactView! : this.view;
  }

  private build(): void {
    if (!this.view) return;
    this.low = this.hexToRgb(this.lowColor);
    this.high = this.hexToRgb(this.highColor);

    const view = this.active();
    this.compactActive = this.useCompact();
    this.max = Math.max(0, ...view.matrix.flat());
    this.colCount = view.cols.labels.length;

    this.legendSwatches = [0, 0.33, 0.66, 1].map((t) => this.colorFor(t * this.max));

    const rowLabels = view.rows.short ?? view.rows.labels;
    this.rowsVm = view.matrix.map((rowArr, y) => ({
      label: rowLabels[y] ?? '',
      cells: rowArr.map((v, x) => ({
        color: this.colorFor(v),
        tooltip: `${view.rows.labels[y]} · ${view.cols.labels[x]} — ${this.valueLabel}: ${v}`,
      })),
    }));

    const tickIdx = view.cols.ticks ?? view.cols.labels.map((_, i) => i);
    const tickLabels = view.cols.short ?? view.cols.labels;
    const last = this.colCount - 1;
    this.colTicksVm = tickIdx.map((i) => ({
      col: i,
      label: tickLabels[i] ?? '',
      align: i === 0 ? 'start' : i === last ? 'end' : 'center',
    }));
  }

  // grid-template-columns string for the cells / ticks grids
  get colTemplate(): string {
    return `repeat(${this.colCount}, 1fr)`;
  }

  private hexToRgb(hex: string): number[] {
    const h = hex.replace('#', '');
    return [0, 2, 4].map((i) => parseInt(h.substr(i, 2), 16));
  }
  private colorFor(v: number): string {
    const t = this.max > 0 ? Math.min(v / this.max, 1) : 0;
    const e = Math.pow(t, 0.6); // ease so mid values stay readable
    const l = (a: number, b: number) => Math.round(a + (b - a) * e);
    return `rgb(${l(this.low[0], this.high[0])},${l(this.low[1], this.high[1])},${l(this.low[2], this.high[2])})`;
  }
}
